import type { BulkObject, BulkObjectType } from "../../api/BulkAPI.types";

export type BulkObjectGroup =
  | "detected"
  | "inserted"
  | "insertedWithWarnings"
  | "failed"
  | "duplicate";

type BulkGroupCounts = Record<BulkObjectGroup, number>;

export interface BulkTypeSummary {
  type: BulkObjectType;
  total: number;
}

export interface BulkSummary {
  byType: BulkTypeSummary[];
  counts: BulkGroupCounts;
  total: number;
}

const BULK_TYPE_ORDER: readonly BulkObjectType[] = [
  "media",
  "casting",
  "smartlist",
  "copyright",
  "media_translation",
  "casting_translation",
];

function hasBulkWarnings(
  object: Pick<BulkObject, "children" | "validation">,
): boolean {
  return [object, ...object.children].some(
    ({ validation }) => validation.summary.warnings > 0,
  );
}

export function getBulkObjectGroup(
  object: Pick<BulkObject, "children" | "status" | "validation">,
): BulkObjectGroup {
  switch (object.status) {
    case "done":
      return hasBulkWarnings(object) ? "insertedWithWarnings" : "inserted";
    case "failed":
    case "error":
      return "failed";
    case "duplicate":
      return "duplicate";
    case "todo":
    case "warning":
    default:
      return "detected";
  }
}

export function getBulkObjectLabel(object: BulkObject): string {
  const { data } = object;

  if ("label" in data) {
    return data.label;
  }

  if ("key" in data) {
    return data.key;
  }

  return object.uuid;
}

function createEmptyCounts(): BulkGroupCounts {
  return {
    detected: 0,
    inserted: 0,
    insertedWithWarnings: 0,
    failed: 0,
    duplicate: 0,
  };
}

function getTypeRank(type: BulkObjectType): number {
  const index = BULK_TYPE_ORDER.indexOf(type);

  return index === -1 ? BULK_TYPE_ORDER.length : index;
}

export function computeBulkSummary(objectList: BulkObject[]): BulkSummary {
  const counts = createEmptyCounts();
  const totalByType = new Map<BulkObjectType, number>();

  objectList.forEach((object) => {
    counts[getBulkObjectGroup(object)] += 1;
    totalByType.set(object.type, (totalByType.get(object.type) ?? 0) + 1);
  });

  const byType = [...totalByType.entries()]
    .map(([type, total]): BulkTypeSummary => ({ type, total }))
    .sort((left, right) => getTypeRank(left.type) - getTypeRank(right.type));

  return { byType, counts, total: objectList.length };
}
